const DEFAULT_PRODUCT_IMAGE = "/images/default-product.jpg";

export const isStructuredImages = (images) =>
  Boolean(images && typeof images === "object" && !Array.isArray(images));

const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const normalizeImagePair = (image, fallback = "") => {
  if (typeof image === "string" && image.trim()) {
    return { thumbnail: image.trim(), full: image.trim() };
  }

  if (!image || typeof image !== "object") {
    return fallback ? { thumbnail: fallback, full: fallback } : null;
  }

  const thumbnail = firstString(
    image.thumbnail,
    image.thumb,
    image.full,
    image.url,
    image.src,
    image.image,
    fallback
  );
  const full = firstString(
    image.full,
    image.thumbnail,
    image.url,
    image.src,
    image.image,
    fallback
  );
  const original = firstString(image.original, image.originalUrl);

  if (!thumbnail && !full && !original) return null;
  return {
    thumbnail: thumbnail || full || original,
    full: full || original || thumbnail,
    ...(original ? { original } : {}),
  };
};

export const getLegacyProductImageUrls = (product) => {
  const urls = [];

  if (typeof product?.image === "string" && product.image.trim()) {
    urls.push(product.image.trim());
  }

  if (typeof product?.imageUrl === "string" && product.imageUrl.trim()) {
    urls.push(product.imageUrl.trim());
  }

  if (Array.isArray(product?.images)) {
    product.images.forEach((image) => {
      if (typeof image === "string" && image.trim()) urls.push(image.trim());
    });
  }

  return [...new Set(urls)];
};

export const getProductImage = (
  product,
  type = "full",
  index = 0,
  placeholder = DEFAULT_PRODUCT_IMAGE
) => {
  const images = product?.images;
  const galleryIndex = Number.isInteger(index) && index >= 0 ? index : 0;

  if (isStructuredImages(images)) {
    const main = images.main || {};
    const galleryItem = Array.isArray(images.gallery)
      ? images.gallery[galleryIndex] || {}
      : {};

    if (type === "thumbnail") {
      return (
        firstString(
          main.thumbnail,
          galleryItem.thumbnail,
          main.full,
          galleryItem.full,
          product?.image,
          product?.imageUrl,
          placeholder
        ) || placeholder
      );
    }

    return (
      firstString(
        main.full,
        galleryItem.full,
        main.thumbnail,
        galleryItem.thumbnail,
        product?.image,
        product?.imageUrl,
        placeholder
      ) || placeholder
    );
  }

  const legacyUrls = getLegacyProductImageUrls(product);
  return legacyUrls[0] || placeholder;
};

export const getProductGalleryImages = (product, placeholder = DEFAULT_PRODUCT_IMAGE) => {
  const images = product?.images;

  if (isStructuredImages(images)) {
    const gallery = [];
    const main = normalizeImagePair(images.main);
    if (main) gallery.push(main);

    if (Array.isArray(images.gallery)) {
      images.gallery.forEach((item) => {
        const normalized = normalizeImagePair(item);
        if (normalized) gallery.push(normalized);
      });
    }

    if (gallery.length) return gallery;
  }

  const legacy = getLegacyProductImageUrls(product)
    .map((url) => normalizeImagePair(url))
    .filter(Boolean);

  return legacy.length ? legacy : [{ thumbnail: placeholder, full: placeholder }];
};

export default getProductImage;
